import { useCallback, useEffect, useRef, useState } from "react";
import {
  Capture,
  canRecord,
  clipStartFor,
  listDevices,
  openStream,
  permissionMessage,
  pickContainer,
  recordingName,
  recordingRef,
  saveRecording,
  stopStream,
  type Container,
  type DeviceChoice,
  type DeviceList,
  type RecordMode,
  type Saved,
} from "./record.ts";
import type { SourceRef } from "./timeline.ts";

/**
 * Recording, as the editor sees it.
 *
 * Arm it and the camera or microphone opens with a live picture; press Record
 * and the timeline plays from the playhead while the take is captured; press
 * Stop and the take is written to disk and laid on the timeline where the
 * playhead was. The devices stay open between takes so a second attempt is one
 * press away, and close when the strip is dismissed.
 *
 * The hook owns the stream and the recorder, which are not React state and
 * live in refs; what the strip needs to draw is mirrored into state.
 */
export type RecorderPhase = "off" | "opening" | "ready" | "recording" | "saving";

export interface RecorderState {
  phase: RecorderPhase;
  mode: RecordMode;
  devices: DeviceList;
  choice: DeviceChoice;
  /** Run the timeline while recording, so a voiceover can follow the picture. */
  playAlong: boolean;
  stream: MediaStream | null;
  /** Whether the open stream has sound in it: a camera can come without a mic. */
  hasAudio: boolean;
  /** performance.now() when the current take began, for the readout. */
  since: number | null;
}

export interface TakeResult {
  saved: Saved;
  ref: SourceRef;
  /** Seconds on the timeline where the take's first moment belongs. */
  start: number;
}

export interface RecorderHooks {
  /** Start playback if asked; report where the playhead was and, if it did start, when. */
  onBegin: (playAlong: boolean) => { at: number; playStartedAt: number | null };
  onEnd: () => void;
  onTake: (take: TakeResult) => void;
  onError: (message: string) => void;
}

const IDLE: RecorderState = {
  phase: "off",
  mode: "camera",
  devices: { cameras: [], mics: [] },
  choice: {},
  playAlong: true,
  stream: null,
  hasAudio: false,
  since: null,
};

export interface RecorderActions {
  arm: () => void;
  disarm: () => void;
  setMode: (mode: RecordMode) => void;
  setChoice: (choice: DeviceChoice) => void;
  setPlayAlong: (on: boolean) => void;
  record: () => void;
  stop: () => void;
}

export function useRecorder(hooks: RecorderHooks): {
  state: RecorderState;
  actions: RecorderActions;
  /** The live picture, so a poster frame can be taken from it on stop. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
} {
  const [state, setState] = useState<RecorderState>(IDLE);
  const stream = useRef<MediaStream | null>(null);
  const container = useRef<Container | null>(null);
  const capture = useRef<Capture | null>(null);
  const take = useRef<{ at: number; playStartedAt: number | null; startedAt: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const latest = useRef(hooks);
  latest.current = hooks;
  // The actions read the current state from here rather than from an updater,
  // so none of them has a side effect React might run twice.
  const current = useRef(state);
  current.current = state;
  // Opening is async; a mode change or dismissal while it runs must win.
  const generation = useRef(0);

  const open = useCallback(async (mode: RecordMode, choice: DeviceChoice) => {
    const mine = (generation.current += 1);
    stopStream(stream.current);
    stream.current = null;
    setState((s) => ({ ...s, phase: "opening", mode, choice, stream: null }));
    const picked = pickContainer(mode, (type) => MediaRecorder.isTypeSupported(type));
    if (!picked) {
      latest.current.onError("This browser cannot record video or audio.");
      setState((s) => ({ ...s, phase: "off" }));
      return;
    }
    container.current = picked;
    try {
      const opened = await openStream(mode, choice);
      if (mine !== generation.current) {
        stopStream(opened);
        return;
      }
      stream.current = opened;
      const devices = await listDevices().catch(() => ({ cameras: [], mics: [] }));
      setState((s) => ({
        ...s,
        phase: "ready",
        devices,
        stream: opened,
        hasAudio: opened.getAudioTracks().length > 0,
        since: null,
      }));
    } catch (error) {
      if (mine !== generation.current) return;
      latest.current.onError(permissionMessage(error));
      setState((s) => ({ ...s, phase: "off", stream: null }));
    }
  }, []);

  const disarm = useCallback(() => {
    generation.current += 1;
    stopStream(stream.current);
    stream.current = null;
    capture.current = null;
    setState((s) => ({ ...IDLE, mode: s.mode, playAlong: s.playAlong }));
  }, []);

  useEffect(() => () => stopStream(stream.current), []);

  const arm = useCallback(() => {
    if (!canRecord()) {
      latest.current.onError("This browser cannot record from a camera or microphone.");
      return;
    }
    if (current.current.phase !== "off") return;
    void open(current.current.mode, current.current.choice);
  }, [open]);

  const busy = () => current.current.phase === "recording" || current.current.phase === "saving";

  const setMode = useCallback((mode: RecordMode) => {
    if (busy() || mode === current.current.mode) return;
    void open(mode, current.current.choice);
  }, [open]);

  const setChoice = useCallback((choice: DeviceChoice) => {
    if (busy()) return;
    void open(current.current.mode, choice);
  }, [open]);

  const setPlayAlong = useCallback((on: boolean) => setState((s) => ({ ...s, playAlong: on })), []);

  const record = useCallback(() => {
    const active = stream.current;
    const chosen = container.current;
    if (!active || !chosen || capture.current || current.current.phase !== "ready") return;
    const started = new Capture(active, chosen);
    capture.current = started;
    const { at, playStartedAt } = latest.current.onBegin(current.current.playAlong);
    started
      .start()
      .then((startedAt) => {
        take.current = { at, playStartedAt, startedAt };
        setState((now) => ({ ...now, phase: "recording", since: startedAt }));
      })
      .catch((error: unknown) => {
        capture.current = null;
        latest.current.onEnd();
        latest.current.onError(error instanceof Error ? error.message : "Recording failed to start");
        setState((now) => ({ ...now, phase: "ready" }));
      });
  }, []);

  const stop = useCallback(() => {
    const running = capture.current;
    const active = stream.current;
    const chosen = container.current;
    const placed = take.current;
    if (!running || !active || !chosen || !placed) return;
    capture.current = null;
    latest.current.onEnd();
    setState((s) => ({ ...s, phase: "saving", since: null }));
    void (async () => {
      const mode: RecordMode = active.getVideoTracks().length > 0 ? "camera" : "mic";
      try {
        const poster = mode === "camera" ? posterFrom(videoRef.current) : undefined;
        const settings = active.getVideoTracks()[0]?.getSettings();
        const picture = settings?.width && settings.height ? { width: settings.width, height: settings.height } : null;
        const result = await running.stop();
        const saved = await saveRecording(result.blob, recordingName(mode, chosen.extension), chosen.extension);
        const ref = await recordingRef(saved.file, mode, result.duration, picture, active.getAudioTracks().length > 0, poster);
        latest.current.onTake({ saved, ref, start: clipStartFor(placed.at, placed.playStartedAt, placed.startedAt) });
      } catch (error) {
        latest.current.onError(error instanceof Error ? error.message : "Could not save the recording");
      } finally {
        setState((s) => ({ ...s, phase: stream.current ? "ready" : "off" }));
      }
    })();
  }, []);

  return { state, actions: { arm, disarm, setMode, setChoice, setPlayAlong, record, stop }, videoRef };
}

const POSTER_WIDTH = 320;

/** A small JPEG of what the camera sees right now, for the project list. */
function posterFrom(video: HTMLVideoElement | null): string | undefined {
  if (!video || video.videoWidth === 0) return undefined;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = POSTER_WIDTH;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * POSTER_WIDTH);
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return undefined;
  }
}

/* ---- the strip ----------------------------------------------------------- */

export function RecordStrip({
  state,
  actions,
  hasTimeline,
}: {
  state: RecorderState;
  actions: RecorderActions;
  /** Whether there is anything to play alongside the take. */
  hasTimeline: boolean;
}) {
  const busy = state.phase === "recording" || state.phase === "saving";
  const elapsed = useElapsed(state.since);

  return (
    <div
      role="region"
      aria-label="Recording"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] border border-danger/40 bg-surface px-3 py-2.5"
    >
      <Segmented
        value={state.mode}
        disabled={busy || state.phase === "opening"}
        onChange={actions.setMode}
        options={[
          { value: "camera", label: "Camera" },
          { value: "mic", label: "Microphone" },
        ]}
      />

      {state.phase === "ready" || busy ? (
        <>
          {state.mode === "camera" && state.devices.cameras.length > 1 ? (
            <DeviceSelect
              label="Camera"
              devices={state.devices.cameras}
              value={state.choice.camera ?? state.stream?.getVideoTracks()[0]?.getSettings().deviceId ?? ""}
              disabled={busy}
              onChange={(camera) => actions.setChoice({ ...state.choice, camera })}
            />
          ) : null}
          {state.devices.mics.length > 1 ? (
            <DeviceSelect
              label="Microphone"
              devices={state.devices.mics}
              value={state.choice.mic ?? state.stream?.getAudioTracks()[0]?.getSettings().deviceId ?? ""}
              disabled={busy}
              onChange={(mic) => actions.setChoice({ ...state.choice, mic })}
            />
          ) : null}
          {state.hasAudio ? <Level stream={state.stream} /> : (
            <span className="text-[12px] text-muted">No microphone: picture only</span>
          )}
        </>
      ) : null}

      {state.phase === "opening" ? <span className="text-[12.5px] text-soft">Opening…</span> : null}

      <div className="ml-auto flex items-center gap-3">
        {hasTimeline && !busy ? (
          <label className="flex items-center gap-1.5 text-[12px] text-soft">
            <input
              type="checkbox"
              checked={state.playAlong}
              onChange={(event) => actions.setPlayAlong(event.target.checked)}
              className="accent-pine"
            />
            Play the timeline while recording
          </label>
        ) : null}
        {state.phase === "recording" ? (
          <span className="flex items-center gap-1.5 font-mono text-[12px] tabular-nums text-ink">
            <span aria-hidden="true" className="breathe inline-block size-2 rounded-full bg-danger" />
            {elapsed}
          </span>
        ) : null}
        {state.phase === "saving" ? <span className="text-[12.5px] text-soft">Saving…</span> : null}
        {state.phase === "recording" ? (
          <button type="button" onClick={actions.stop} className="pill-solid !border-danger !bg-danger">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={actions.record}
            disabled={state.phase !== "ready"}
            className="pill-solid !border-danger !bg-danger"
          >
            <span aria-hidden="true" className="inline-block size-2.5 rounded-full bg-cream" />
            Record
          </button>
        )}
        <button type="button" onClick={actions.disarm} disabled={busy} className="pill" aria-label="Close recording">
          Close
        </button>
      </div>

      {hasTimeline && state.playAlong && state.hasAudio && !busy ? (
        <p className="basis-full text-[11.5px] leading-[1.5] text-muted">
          Wear headphones, or the microphone will pick up the playback.
        </p>
      ) : null}
    </div>
  );
}

function useElapsed(since: number | null): string {
  const [, tick] = useState(0);
  useEffect(() => {
    if (since === null) return;
    const timer = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(timer);
  }, [since]);
  if (since === null) return "0:00";
  const seconds = Math.floor((performance.now() - since) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" className="flex rounded-full border border-rule bg-cream-2 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors disabled:opacity-60 ${
            option.value === value ? "bg-surface text-pine shadow-sm" : "text-soft hover:text-pine"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function DeviceSelect({
  label,
  devices,
  value,
  disabled,
  onChange,
}: {
  label: string;
  devices: MediaDeviceInfo[];
  value: string;
  disabled?: boolean;
  onChange: (deviceId: string) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className="max-w-[180px] truncate rounded-[6px] border border-rule bg-surface px-2 py-1 text-[12px] text-ink focus:border-pine focus:outline-none"
    >
      {devices.map((device, index) => (
        <option key={device.deviceId} value={device.deviceId}>
          {device.label || `${label} ${index + 1}`}
        </option>
      ))}
    </select>
  );
}

/**
 * A level meter for the microphone, so a person can see it hears them before
 * they start. Drawn straight to the DOM every frame; nothing else needs the
 * number. The mic is never routed to the speakers, only to the analyser.
 */
function Level({ stream }: { stream: MediaStream | null }) {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) return;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i += 1) sum += (samples[i] ?? 0) ** 2;
      // Roughly decibels mapped onto the bar: -50 dB is silence, 0 dB is full.
      const rms = Math.sqrt(sum / samples.length);
      const level = Math.max(0, Math.min(1, 1 + (20 * Math.log10(rms || 1e-6)) / 50));
      if (bar.current) bar.current.style.transform = `scaleX(${level})`;
    };
    draw();
    return () => {
      cancelAnimationFrame(frame);
      void context.close();
    };
  }, [stream]);

  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-soft">
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      </svg>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-cream-2">
        <div ref={bar} className="h-full w-full origin-left rounded-full bg-rope transition-transform duration-75" />
      </div>
    </div>
  );
}

/* ---- the picture --------------------------------------------------------- */

/**
 * What the camera sees, in the corner of the preview where a reaction cam
 * would go, or filling it when there is no timeline yet to look at. Mirrored,
 * because that is how people expect to see themselves; the recording is not.
 */
export function CameraPeek({
  stream,
  videoRef,
  recording,
  large,
}: {
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  recording: boolean;
  large: boolean;
}) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => undefined);
    return () => {
      video.srcObject = null;
    };
  }, [stream, videoRef]);

  if (!stream || stream.getVideoTracks().length === 0) return null;
  return (
    <div
      className={
        large
          ? "absolute inset-0 flex items-center justify-center bg-ink/90"
          : "absolute bottom-[4%] right-[3%] w-[28%] overflow-hidden rounded-[8px] border-2 border-cream/80 shadow-lg"
      }
    >
      <video
        ref={videoRef}
        muted
        playsInline
        className={large ? "max-h-full max-w-full -scale-x-100" : "block w-full -scale-x-100"}
      />
      {recording ? (
        <span className="breathe absolute left-2 top-2 size-2.5 rounded-full bg-danger shadow" aria-hidden="true" />
      ) : null}
    </div>
  );
}
